import { pickBy } from "lodash";
import { getErrorMessage, isPopulatedArray, isValidString } from "./funcs";
import axios, { AxiosError, AxiosResponse, AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios'

const API_URL = process.env.REACT_APP_API_URL

export const processNoteValues = (values: any, target: object) => {
  if (! (values && target)) return;

  if (values?.note && isValidString(values.note)) Object.assign(target, { note : values.note});

  if (values?.noteAttachments && isPopulatedArray(values.noteAttachments)) {
    Object.assign(target, { noteAttachments : values.noteAttachments.map( (o:any) => o.id)});
  }
}

interface ApiResponse {
  success?: boolean;
  message?: string;
  // Optional headers property supporting raw string maps or Axios header objects
  headers?: RawAxiosResponseHeaders | AxiosResponseHeaders | Record<string, string>;
}

export const handleResponseStatus = <TRes extends ApiResponse>(
  res: AxiosResponse<TRes>,
  setStatus: (status?: string) => void,
  onSuccess: (res?: AxiosResponse<TRes>) => void,
) => {
  if (!res.data?.success) {
    setStatus(res.data?.message ?? 'Something happened');
    return;
  }  
  onSuccess(res);  
};

type RequestFn<TData, TRes> = (data: TData) => Promise<AxiosResponse<TRes>>;
type EndpointOrFn<TData, TRes> = string | RequestFn<TData, TRes>;

export async function doFormSubmit<TData = unknown, TRes extends ApiResponse = ApiResponse>(
  endpointOrFn: EndpointOrFn<TData, TRes>,
  data: TData,  
  setStatus: (status?: string) => void,
  setSubmitting: (isSubmitting: boolean) => void,
  onSuccess: (res?: AxiosResponse<TRes>) => void,
  debug?: boolean
) {

  setStatus('');

  if (debug && (process.env.NODE_ENV === 'development')) {
    console.log(data);
    setSubmitting(false);
    return;
  }
  
  setSubmitting(true);

  try {
    let res: AxiosResponse<TRes>;

    if (typeof endpointOrFn === 'string') {
      // String route -> default to standard axios post
      res = await axios.post<TRes>(`${API_URL}/${endpointOrFn}`, data);
    } else {
      // Function route -> execute the provided request function
      res = await endpointOrFn(data);
    }
    handleResponseStatus(res, setStatus, onSuccess);
  } catch (err) {
    setStatus(getErrorMessage(err as AxiosError));
  } finally {
    setSubmitting(false);
  }
}


export function reloadDraftVars<T extends Record<string, any>>(
  obj: T,
  draftVars: Record<string, any> | null | undefined,
  exclude: string[] = []
): void {
  if (!draftVars || typeof draftVars !== 'object') {
    return;
  }

  // Filter draftVars down to only valid keys
  const validDraftVars = pickBy(draftVars, (value, key) => {
    return (
      !exclude.includes(key) &&
      Object.hasOwn(obj, key) &&
      Boolean(value)
    );
  });

  // Safely merge into obj
  Object.assign(obj, validDraftVars);
}

export interface AddressObject {
  address1?: string | null;
  address2?: string | null;
  address3?: string | null;
  [key: string]: unknown; // Allows other fields on the address object
}

export function splitAddressLines(obj: AddressObject): void {
  const { address1, address2, address3 } = obj;

  // Only run if address1 has a valid string AND address2 & address3 are empty/invalid
  if (!isValidString(address1) || isValidString(address2) || isValidString(address3)) {
    return;
  }

  const parts = address1.split(',').map((str) => str.trim()).filter(Boolean);

  if (parts.length === 0) return;

  obj.address1 = parts[0];
  if (parts.length > 1) obj.address2 = parts[1];
  if (parts.length > 2) obj.address3 = parts.slice(2).join(', ');
}